import React, { useMemo, useState } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useClinicData } from "../../context/ClinicDataContext";
import Modal from "../../components/Modal/Modal";
import { formatRM } from "../../utils/format";

const TreatmentsPage = () => {
  const { treatments, appointments } = useClinicData();
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // treatment object, or {} for new

  const categories = useMemo(() => ["All", ...new Set(treatments.map((t) => t.category))], [treatments]);
  const bookings = useMemo(() => {
    const counts = {};
    appointments.forEach((a) => {
      if (a.status !== "cancelled") a.treatmentIds.forEach((id) => (counts[id] = (counts[id] || 0) + 1));
    });
    return counts;
  }, [appointments]);

  const rows = treatments.filter(
    (t) =>
      (category === "All" || t.category === category) &&
      t.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div>
      <div className="toolbar">
        <div className="toolbar__group">
          <div className="search">
            <IconSearch className="search__icon" size={18} stroke={1.75} />
            <label className="visually-hidden" htmlFor="treatment-search">
              Search treatments
            </label>
            <input
              id="treatment-search"
              className="search__input"
              type="search"
              placeholder="Search treatments"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <label className="form-field">
            <span className="visually-hidden">Category</span>
            <select className="form-field__control" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === "All" ? "All categories" : c}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button className="btn btn--primary" type="button" onClick={() => setEditing({})}>
          <IconPlus size={18} stroke={2} />
          Add treatment
        </button>
      </div>

      <div className="panel panel--flush">
        <div className="data-table__wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Treatment</th>
                <th>Category</th>
                <th className="data-table__num">Duration</th>
                <th className="data-table__num">Price</th>
                <th className="data-table__num">Bookings</th>
                <th>
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td className="data-table__primary">{t.name}</td>
                  <td>
                    <span className="badge badge--neutral">{t.category}</span>
                  </td>
                  <td className="data-table__num">{t.duration} min</td>
                  <td className="data-table__num">{formatRM(t.price)}</td>
                  <td className="data-table__num">{bookings[t.id] || 0}</td>
                  <td className="data-table__num">
                    <button className="btn btn--ghost btn--sm" type="button" onClick={() => setEditing(t)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="empty-state">
              <p className="empty-state__title">No treatments found</p>
              <p>Try another category, or add the treatment to your price list.</p>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <TreatmentModal
          treatment={editing}
          categories={categories.filter((c) => c !== "All")}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

const TreatmentModal = ({ treatment, categories, onClose }) => {
  const { saveTreatment } = useClinicData();
  const isNew = !treatment.id;
  const [form, setForm] = useState({
    name: treatment.name || "",
    category: treatment.category || categories[0],
    duration: treatment.duration || 30,
    price: treatment.price ?? "",
  });
  const [error, setError] = useState("");
  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError("");
  };

  const handleSave = () => {
    if (!form.name.trim() || form.price === "") {
      setError("Enter a treatment name and price.");
      return;
    }
    saveTreatment({
      ...treatment,
      name: form.name.trim(),
      category: form.category,
      duration: Number(form.duration),
      price: Number(form.price),
    });
    onClose();
  };

  return (
    <Modal
      title={isNew ? "Add treatment" : "Edit treatment"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" type="button" onClick={handleSave}>
            {isNew ? "Add treatment" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label className="form-field form-grid__full">
          <span className="form-field__label">Treatment name</span>
          <input className="form-field__control" value={form.name} onChange={update("name")} />
        </label>
        <label className="form-field form-grid__full">
          <span className="form-field__label">Category</span>
          <select className="form-field__control" value={form.category} onChange={update("category")}>
            {categories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Duration</span>
          <select className="form-field__control" value={form.duration} onChange={update("duration")}>
            {[15, 30, 45, 60, 90, 120].map((m) => (
              <option key={m} value={m}>
                {m} minutes
              </option>
            ))}
          </select>
          <span className="form-field__hint">Blocks this much time on the calendar.</span>
        </label>
        <label className="form-field">
          <span className="form-field__label">Price (RM)</span>
          <input
            className="form-field__control"
            type="number"
            min="0"
            step="1"
            inputMode="decimal"
            value={form.price}
            onChange={update("price")}
          />
        </label>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default TreatmentsPage;
